import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { FlexBox, useToasts } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { API, TagsAPI, Endpoints } from '@brg/api';
import {
  MainTitle
} from '../sendProjects/styles';
import { ModalClose } from 'core/modules/modal.module';
import { CloseModalIcon } from 'core/modules/CloseIconNew';
import { SendProjectButton } from '_competition/components/SendProjectButton';
import { AppTester } from 'supporting';
import { BTag } from 'core/components/bTag/bTag';
import { SearchIcon } from 'shared/icons';
import SlideNavigation from 'shared/components/slideNavigation/slideNavigation';
import {
  CanonicalDomainModalWrapper,
  CanonicalDomainModalContainer,
  ScrollableDomainsContainer,
  SelectedDomainsWrapper,
  SelectedDomainsTitle,
  SelectedDomainsContainer,
  DomainsContainer,
  LoadingContainer,
  DomainsGrid
} from './CanonicalDomainModal.styles';
import { TagsSearchInputWrapper, TagsSearchInput } from '../selectTagsModal/styles';

interface IDomain {
  url: string;
  platform: string;
  stage?: string;
  dev?: string;
}

interface CanonicalDomainModalProps {
  selectedDomains: string[];
  onSave: (domains: string[]) => void;
  onClose: () => void;
}

export const CanonicalDomainModal: FC<CanonicalDomainModalProps> = ({
  selectedDomains,
  onSave,
  onClose
}) => {
  const { t } = useTranslation();
  const toasts = useToasts();
  const theme = useTheme();
  const isGeo = AppTester.isGeograffee();

  const [searchQuery, setSearchQuery] = useState('');
  const [availableDomains, setAvailableDomains] = useState<IDomain[]>([]);
  const [selectedDomainsState, setSelectedDomainsState] = useState<string[]>(selectedDomains);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [activePlatform, setActivePlatform] = useState<string>('');

  const ALLOWED_PLATFORMS = ['gallery', 'deziiign'];

  const fetchDomains = useCallback(async () => {
    if (isLoading || isDataLoaded) {
      return;
    }
    
    try {
      setIsLoading(true);

      const response = await API.fetch(`${Endpoints.tagsMnePw}/api/Domain/GetAllDomains`, {})
        .then(res => res.json());

      const processItem = (item: any): IDomain | null => {
        if (typeof item === 'string') return { url: item, platform: 'unknown' };
        
        const domainUrl = item?.url || item?.stage || item?.dev;
        if (!domainUrl) return null;
        
        let hostname = domainUrl;
        try {
          hostname = new URL(domainUrl).hostname;
        } catch (e) {
          // Используем domainUrl как есть
        }
        
        return {
          url: hostname,
          platform: item.platform || 'unknown'
        };
      };

      const domains: IDomain[] = Array.isArray(response) 
        ? response
            .map(processItem)
            .filter((domain): domain is IDomain => 
              domain !== null && domain.url !== '' && domain.url !== 'unknown'
            )
            .reduce((acc: IDomain[], current) => {
              if (!acc.find(d => d.url === current.url)) {
                acc.push(current);
              }
              return acc;
            }, [])
        : [];

      setAvailableDomains(domains);
      setIsLoading(false);
      setIsDataLoaded(true);
    } catch (error) {
              toasts.error(t('tags_modal.canonical_domain.loading_domains_error'));
      setIsLoading(false);
    }
  }, [isLoading, toasts, isDataLoaded]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  useEffect(() => {
    if (isDataLoaded && availableDomains.length > 0) {
      const validSelectedDomains = selectedDomains.filter(domain => 
        domain && domain !== 'unknown' && availableDomains.some(d => d.url === domain)
      );
      
      if (validSelectedDomains.length !== selectedDomains.length) {
        setSelectedDomainsState(validSelectedDomains);
      }
    }
  }, [selectedDomains, isDataLoaded, availableDomains]);

  const platforms = useMemo(() => 
    Array.from(new Set(availableDomains.map(d => d.platform)))
      .filter(p => ALLOWED_PLATFORMS.includes(p)),
    [availableDomains]
  );

  useEffect(() => {
    if (platforms.length > 0 && !activePlatform) {
      setActivePlatform(platforms[0]);
    }
  }, [platforms, activePlatform]);

  const platformNavigation = useMemo(() => {
    const platformLabels: Record<string, string> = {
      gallery: t('tags_modal.canonical_domain.gallery', 'gallllery'),
      deziiign: t('tags_modal.canonical_domain.design', 'deziiign')
    };
    
    return platforms.reduce((acc, platform) => {
      acc[platform] = platformLabels[platform] || platform;
      return acc;
    }, {} as Record<string, string>);
  }, [platforms, t]);

  const filteredDomains = useMemo(() => 
    availableDomains.filter(domain => 
      domain.url.toLowerCase().includes(searchQuery.toLowerCase()) &&
      domain.platform === activePlatform
    ),
    [availableDomains, searchQuery, activePlatform]
  );

  const addDomain = (domainUrl: string) => {
    if (selectedDomainsState.includes(domainUrl)) return;
    
    const domainToAdd = availableDomains.find(d => d.url === domainUrl);
    if (!domainToAdd) return;

    const selectedDomainsWithPlatforms = selectedDomainsState
      .map(selectedUrl => availableDomains.find(d => d.url === selectedUrl))
      .filter(Boolean) as IDomain[];

    const hasSamePlatform = selectedDomainsWithPlatforms.some(selectedDomain => 
      selectedDomain.platform === domainToAdd.platform && 
      ALLOWED_PLATFORMS.includes(selectedDomain.platform)
    );

    if (hasSamePlatform) {
      toasts.error(t('tags_modal.canonical_domain.platform_validation_error', { 
        platform: domainToAdd.platform 
      }));
      return;
    }

    setSelectedDomainsState(prev => [...prev, domainUrl]);
  };

  const removeDomain = (domain: string) => 
    setSelectedDomainsState(prev => prev.filter(d => d !== domain));

  const handleSave = () => {
    onSave(selectedDomainsState);
    onClose();
  };

  return (
    <CanonicalDomainModalWrapper>
      <CanonicalDomainModalContainer>
        <FlexBox width="100%" direction="column">
          <FlexBox justify='space-between' offsetBottom={32}>
            <MainTitle>{t('tags_modal.canonical_domain.title')}</MainTitle>
            <SendProjectButton isGreen={isGeo} width={187} onClick={handleSave}>
              {t('competition.save')}
            </SendProjectButton>
          </FlexBox>

          <ModalClose onClick={onClose}>
            <CloseModalIcon />
          </ModalClose>

          <TagsSearchInputWrapper gx={12} offsetBottom={16}>
            <SearchIcon fill={theme.tags.modal.search.color}/>
            <TagsSearchInput
              value={searchQuery}
              onChange={(e: any) => setSearchQuery(e.target.value)}
              placeholder={t('tags_modal.canonical_domain.search_placeholder')}
            />
          </TagsSearchInputWrapper>

          {selectedDomainsState.length > 0 && (
            <SelectedDomainsWrapper>
              <SelectedDomainsTitle>
                {t('tags_modal.canonical_domain.selected_domain')}
              </SelectedDomainsTitle>
              <SelectedDomainsContainer gx={8}>
                {selectedDomainsState.map((domain) => (
                  <BTag
                    key={domain}
                    removable
                    showRemoveIcon
                    onIconClick={() => removeDomain(domain)}
                  >
                    {domain}
                  </BTag>
                ))}
              </SelectedDomainsContainer>
            </SelectedDomainsWrapper>
          )}

          {platforms.length > 0 && (
            <FlexBox offsetTop={28} offsetTopMobile={16}>
              <SlideNavigation
                items={platformNavigation}
                activeIndexKey={activePlatform}
                onChange={setActivePlatform}
                autoWidth
                smallTab
              />
            </FlexBox>
          )}

          <DomainsContainer>
            {isLoading ? (
              <LoadingContainer>
                {t('tags_modal.canonical_domain.loading_domains')}
              </LoadingContainer>
            ) : activePlatform ? (
              <ScrollableDomainsContainer>
                <DomainsGrid>
                  {filteredDomains.map((domain) => (
                    <BTag
                      key={domain.url}
                      removable
                      selected={selectedDomainsState.includes(domain.url)}
                      onClick={() => addDomain(domain.url)}
                    >
                      {domain.url}
                    </BTag>
                  ))}
                </DomainsGrid>
              </ScrollableDomainsContainer>
            ) : (
              <LoadingContainer>
                {t('tags_modal.canonical_domain.select_platform')}
              </LoadingContainer>
            )}
          </DomainsContainer>
        </FlexBox>
      </CanonicalDomainModalContainer>
    </CanonicalDomainModalWrapper>
  );
};
import React, { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { FlexBox, useToasts } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { API, TagsAPI, Endpoints } from '@brg/api';
import {
  MainTitle
} from '../sendProjects/styles';
import { ModalClose } from 'core/modules/modal.module';
import { CloseModalIcon } from 'core/modules/CloseIconNew';
import { SendProjectButton } from '_competition/components/SendProjectButton';
import { AppTester } from 'supporting';
import { BTag } from 'core/components/bTag/bTag';
import { SearchIcon } from 'shared/icons';
import SlideNavigation from 'shared/components/slideNavigation/slideNavigation';
import {
  CanonicalDomainModalWrapper,
  CanonicalDomainModalContainer,
  ScrollableDomainsContainer,
  SelectedDomainsWrapper,
  SelectedDomainsTitle,
  SelectedDomainsContainer,
  DomainsContainer,
  LoadingContainer,
  DomainsGrid
} from './CanonicalDomainModal.styles';
import { TagsSearchInputWrapper, TagsSearchInput } from '../selectTagsModal/styles';

interface IDomain {
  url: string;
  platform: string;
  stage?: string;
  dev?: string;
}

interface CanonicalDomainModalProps {
  selectedDomains: string[];
  onSave: (domains: string[]) => void;
  onClose: () => void;
}

export const CanonicalDomainModal: FC<CanonicalDomainModalProps> = ({
  selectedDomains,
  onSave,
  onClose
}) => {
  const { t } = useTranslation();
  const toasts = useToasts();
  const theme = useTheme();
  const isGeo = AppTester.isGeograffee();

  const [searchQuery, setSearchQuery] = useState('');
  const [availableDomains, setAvailableDomains] = useState<IDomain[]>([]);
  const [selectedDomainsState, setSelectedDomainsState] = useState<string[]>(selectedDomains);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [activePlatform, setActivePlatform] = useState<string>('');

  const ALLOWED_PLATFORMS = ['gallery', 'deziiign'];

  const fetchDomains = useCallback(async () => {
    if (isLoading || isDataLoaded) {
      return;
    }
    
    try {
      setIsLoading(true);

      const response = await API.fetch(`${Endpoints.tagsMnePw}/api/Domain/GetAllDomains`, {})
        .then(res => res.json());

      const processItem = (item: any): IDomain | null => {
        if (typeof item === 'string') return { url: item, platform: 'unknown' };
        
        const domainUrl = item?.url || item?.stage || item?.dev;
        if (!domainUrl) return null;
        
        let hostname = domainUrl;
        try {
          hostname = new URL(domainUrl).hostname;
        } catch (e) {
          // Используем domainUrl как есть
        }
        
        return {
          url: hostname,
          platform: item.platform || 'unknown'
        };
      };

      const domains: IDomain[] = Array.isArray(response) 
        ? response
            .map(processItem)
            .filter((domain): domain is IDomain => 
              domain !== null && domain.url !== '' && domain.url !== 'unknown'
            )
            .reduce((acc: IDomain[], current) => {
              if (!acc.find(d => d.url === current.url)) {
                acc.push(current);
              }
              return acc;
            }, [])
        : [];

      setAvailableDomains(domains);
      setIsLoading(false);
      setIsDataLoaded(true);
    } catch (error) {
              toasts.error(t('tags_modal.canonical_domain.loading_domains_error'));
      setIsLoading(false);
    }
  }, [isLoading, toasts, isDataLoaded]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  useEffect(() => {
    if (isDataLoaded && availableDomains.length > 0) {
      const validSelectedDomains = selectedDomains.filter(domain => 
        domain && domain !== 'unknown' && availableDomains.some(d => d.url === domain)
      );
      
      if (validSelectedDomains.length !== selectedDomains.length) {
        setSelectedDomainsState(validSelectedDomains);
      }
    }
  }, [selectedDomains, isDataLoaded, availableDomains]);

  const platforms = useMemo(() => 
    Array.from(new Set(availableDomains.map(d => d.platform)))
      .filter(p => ALLOWED_PLATFORMS.includes(p)),
    [availableDomains]
  );

  useEffect(() => {
    if (platforms.length > 0 && !activePlatform) {
      setActivePlatform(platforms[0]);
    }
  }, [platforms, activePlatform]);

  const platformNavigation = useMemo(() => {
    const platformLabels: Record<string, string> = {
      gallery: t('tags_modal.canonical_domain.gallery', 'gallllery'),
      deziiign: t('tags_modal.canonical_domain.design', 'deziiign')
    };
    
    return platforms.reduce((acc, platform) => {
      acc[platform] = platformLabels[platform] || platform;
      return acc;
    }, {} as Record<string, string>);
  }, [platforms, t]);

  const filteredDomains = useMemo(() => 
    availableDomains.filter(domain => 
      domain.url.toLowerCase().includes(searchQuery.toLowerCase()) &&
      domain.platform === activePlatform
    ),
    [availableDomains, searchQuery, activePlatform]
  );

  const addDomain = (domainUrl: string) => {
    if (selectedDomainsState.includes(domainUrl)) return;
    
    const domainToAdd = availableDomains.find(d => d.url === domainUrl);
    if (!domainToAdd) return;

    const selectedDomainsWithPlatforms = selectedDomainsState
      .map(selectedUrl => availableDomains.find(d => d.url === selectedUrl))
      .filter(Boolean) as IDomain[];

    const hasSamePlatform = selectedDomainsWithPlatforms.some(selectedDomain => 
      selectedDomain.platform === domainToAdd.platform && 
      ALLOWED_PLATFORMS.includes(selectedDomain.platform)
    );

    if (hasSamePlatform) {
      toasts.error(t('tags_modal.canonical_domain.platform_validation_error', { 
        platform: domainToAdd.platform 
      }));
      return;
    }

    setSelectedDomainsState(prev => [...prev, domainUrl]);
  };

  const removeDomain = (domain: string) => 
    setSelectedDomainsState(prev => prev.filter(d => d !== domain));

  const handleSave = () => {
    onSave(selectedDomainsState);
    onClose();
  };

  return (
    <CanonicalDomainModalWrapper>
      <CanonicalDomainModalContainer>
        <FlexBox width="100%" direction="column">
          <FlexBox justify='space-between' offsetBottom={32}>
            <MainTitle>{t('tags_modal.canonical_domain.title')}</MainTitle>
            <SendProjectButton isGreen={isGeo} width={187} onClick={handleSave}>
              {t('competition.save')}
            </SendProjectButton>
          </FlexBox>

          <ModalClose onClick={onClose}>
            <CloseModalIcon />
          </ModalClose>

          <TagsSearchInputWrapper gx={12} offsetBottom={16}>
            <SearchIcon fill={theme.tags.modal.search.color}/>
            <TagsSearchInput
              value={searchQuery}
              onChange={(e: any) => setSearchQuery(e.target.value)}
              placeholder={t('tags_modal.canonical_domain.search_placeholder')}
            />
          </TagsSearchInputWrapper>

          {selectedDomainsState.length > 0 && (
            <SelectedDomainsWrapper>
              <SelectedDomainsTitle>
                {t('tags_modal.canonical_domain.selected_domain')}
              </SelectedDomainsTitle>
              <SelectedDomainsContainer gx={8}>
                {selectedDomainsState.map((domain) => (
                  <BTag
                    key={domain}
                    removable
                    showRemoveIcon
                    onIconClick={() => removeDomain(domain)}
                  >
                    {domain}
                  </BTag>
                ))}
              </SelectedDomainsContainer>
            </SelectedDomainsWrapper>
          )}

          {platforms.length > 0 && (
            <FlexBox offsetTop={28} offsetTopMobile={16}>
              <SlideNavigation
                items={platformNavigation}
                activeIndexKey={activePlatform}
                onChange={setActivePlatform}
                autoWidth
                smallTab
              />
            </FlexBox>
          )}

          <DomainsContainer>
            {isLoading ? (
              <LoadingContainer>
                {t('tags_modal.canonical_domain.loading_domains')}
              </LoadingContainer>
            ) : activePlatform ? (
              <ScrollableDomainsContainer>
                <DomainsGrid>
                  {filteredDomains.map((domain) => (
                    <BTag
                      key={domain.url}
                      removable
                      selected={selectedDomainsState.includes(domain.url)}
                      onClick={() => addDomain(domain.url)}
                    >
                      {domain.url}
                    </BTag>
                  ))}
                </DomainsGrid>
              </ScrollableDomainsContainer>
            ) : (
              <LoadingContainer>
                {t('tags_modal.canonical_domain.select_platform')}
              </LoadingContainer>
            )}
          </DomainsContainer>
        </FlexBox>
      </CanonicalDomainModalContainer>
    </CanonicalDomainModalWrapper>
  );
};
